<template>
    <div id="modal-create-tag">
        <div class="rp-overlay"></div>
        <div v-if="!layout.showDeleteModal" class="rp-modal" style="width: 470px;">
            <div class="rp-modal-header">
                <h3 class="rp-modal-title">{{ $t('Edit tag') }}</h3>
                <button class="rp-btn-dismiss" @click="$emit('closeModal')">
                    <XIcon class="w-5 h-5" />
                </button>
            </div>
            <div class="bg-white p-5">

				<!-- Name -->
				<div class="flex flex-col gap-1">
					<div class="font-medium text-sm">{{ $t('Name') }} *</div>
					<input type="text" class="form-control border-transparent mt-2 py-3 px-4 pr-10 bg-gray-200 text-gray-700" 
                        :disabled="!enableInputTagName"
						:placeholder="'Tag name'" v-model="layout.currentTag.name" required />
					<div v-if="!enableEditButton" class="text-xs font-medium mt-2 text-red-600">
						{{ errorMessage }}
					</div>
				</div>

				<!-- Color -->
				<div class="mt-5">
                    <div class="font-medium text-sm flex">
						{{ $t('Color') }} *
						<div class="text-xs w-5 h-5 rounded-full px-2 py-1 text-center text-gray-800 ml-auto" :style="'background-color: ' + (colorHex || '') + ' !important'"></div>
						<span class="text-xs pl-2 pt-0.5 text-gray-500">{{ colorHex || '' }}</span>
					</div>
                    <div v-if="colorHex" class="w-full sliderBox mt-5">
						<Slider v-model="colorHex" class="mt-3 w-full" :size="'small'" />
					</div>
				</div>

				<!-- Description -->
				<div class="mt-5">
					<div class="font-medium text-sm">{{ $t('Description') }}</div>
					<textarea class="form-control py-3 px-4 border-transparent pr-10 bg-gray-200 text-gray-700 h-20 mt-2" 
						:placeholder="'Tag description'" v-model="layout.currentTag.description" required></textarea>
				</div>

				<!-- Created date -->
				<div v-if="layout.currentTag.createdAt" class="text-xs mt-3 text-gray-600 font-italic">
					<ClockIcon class="w-4 h-4 align-top" /> {{ $t('Created on') }} {{ layout.currentTag.createdAt }}
				</div>

                <!-- Action buttons -->
                <div class="rp-modal-body-buttons gap-3 mt-4">
                    <button v-if="layout.dataset.type !== 'ANOMALY'" class="rp-btn-danger" @click="layout.showDeleteModal = true"
                        :disabled="!enableEditButton">
                        {{ $t('Delete') }}
                    </button>
                    <button class="rp-btn-primary" @click="editTag()"
                        :disabled="!enableEditButton">
                        {{ $t('Save') }}
                    </button>
                </div>

            </div>
        </div>
        <DeleteTagModal v-if="layout.showDeleteModal" 
			:dataset="layout.dataset"
            :currentTag="layout.currentTag"
			:tagID="layout.edit"
            @closeModal="layout.showDeleteModal = false"
			@tagDeleted="layout.showDeleteModal = false; layout.edit = false; emit('tagEdited')"/>
    </div>
</template>

<script setup>
import { onMounted, computed, ref } from "vue";
import { Slider } 			        from '@ckpack/vue-color';
import * as rosepetalModel	        from "rosepetal-model";

import DeleteTagModal 	from "@/modals/tag/DeleteTag.vue";

let $dataset = rosepetalModel.default.dataset;

const emit = defineEmits(['closeModal, tagEdited']);

const props = defineProps({
    dataset: {
        type: Object,
        required: true,
    },
    tagID: {
        type: String,
        required: true,
    }
});

const layout = ref({
    dataset:            {},
    currentTag:         {},
    showDeleteModal:    false
});

const colorHex = computed({
    get() {
        if (layout.value.currentTag.color) {
            const color = layout.value.currentTag.color;
            return color;
        }
        return "#000000";
    },
    set(newHex) {
        const newColor = newHex.hex8?.substring(0, 7) || "#000000";
        layout.value.currentTag.color = newColor;
    }
});

const reservedNames = computed(() => {
    const type = layout.value.dataset.type;
    if (type === 'MULTICLASS' || type === 'imageObjectDetection' || type === 'ANOMALY') {
        return ["0", "unclassified"];
    } else if (type === 'MULTILABEL') {
        return ["0", "anomaly", "ok", "normal"];
    }
    return [];
});

const checkReservedNames = (name) => {
	return reservedNames.value.includes(name.toLowerCase());
};

const checkDuplicatedName = (name) => {
    const tags = layout.value.dataset.tags || {};
    return Object.values(tags).some(tag => tag.id.toLowerCase() == name.toLowerCase() && tag.id != layout.value.currentTag.id);
};

const isValidTagName = (name) => {
    return !(checkReservedNames(name) || checkDuplicatedName(name));
};

const enableEditButton = computed(() => {
    if (reservedNames.value.includes(layout.value.currentTag.id)) return false;
    const name = layout.value.currentTag?.name?.trim();
    return !!name && isValidTagName(name);
});

const enableInputTagName = computed(() => {
    if (layout.value.currentTag.id)
        return !reservedNames.value.includes(layout.value.currentTag.id.toLowerCase());
    return false;
});

const errorMessage = computed(() => {
    const currentName = layout.value.currentTag.name;
    if (!currentName || !enableInputTagName.value) return "";
    if (!currentName.trim()) return "Invalid tag name";
    if (checkReservedNames(currentName)) return "Tag name is reserved";
    if (checkDuplicatedName(currentName)) return "Tag already exists";
    return "";
});

const editTag = async () => { 
    const tagOpt = {
        id: 			layout.value.currentTag.id,
        name: 			layout.value.currentTag.name,
        color:			layout.value.currentTag.color, 
        description:	layout.value.currentTag.description || ""
    }
    await $dataset.updateTag(layout.value.dataset, tagOpt);
	emit("tagEdited");
}

onMounted(async () => {
    if (props.dataset) {
        layout.value.dataset = props.dataset;
    }
    if (props.tagID) {
        layout.value.currentTag = await $dataset.getTag(layout.value.dataset.id, props.tagID);
    }
});
</script>